/**
 * BOLev — Breakout Level projector (Round 112).
 *
 * Projects upcoming BUY / SHORT breakout levels from higher-timeframe CLH
 * candles (Daily for BOLev_D, Weekly for BOLev_W), independent of the chart's
 * own timeframe and candle type.
 *
 * Per period candle (using only COMPLETED candles for levels → no repaint):
 *   buyLevel   = max(high of previous `lookback` completed CLH candles)
 *   shortLevel = min(low  of previous `lookback` completed CLH candles)
 *
 * State (dual-track, sides independent):
 *   BUY  : off → lookout (a CLH candle has open == high, i.e. opened at its top)
 *               → zone   (current period high > buyLevel)
 *               → lookout (a later open == high re-arms)
 *   SHORT: off → lookout (open == low) → zone (low < shortLevel) → lookout
 *   Opposite flip: when one side confirms a ZONE breakout, the other side is
 *   reset to off (a confirmed bull breakout invalidates a pending bear setup,
 *   and vice versa).
 *
 * `open == high` is exact for CLH candles because CLH high = max(rawHigh, open,
 * close); so open == high ⟺ open ≥ rawHigh and open ≥ close (no float epsilon).
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';

export type BolevState = 'off' | 'lookout' | 'zone';

export interface BolevPeriod {
  time: number;                 // period (CLH) candle time, IST-shifted
  buyLevel: number | null;      // max of previous `lookback` highs (null until enough data)
  shortLevel: number | null;    // min of previous `lookback` lows
  buy: BolevState;              // BUY side state DURING this period
  short: BolevState;            // SHORT side state DURING this period
  buyBreak: boolean;            // BUY zone first activated in this period
  shortBreak: boolean;          // SHORT zone first activated in this period
  high: number;                 // CLH high of this period (for band drawing)
  low: number;                  // CLH low of this period
  /** R115 — strict no-wick breakout (independent of the lookout/zone machine).
   *  cond3Buy   : CLH `low === open`  AND high  > buyLevel   → strong LONG candle
   *  cond3Short : CLH `high === open` AND low   < shortLevel → strong SHORT candle
   *  Used by RockBand as its third entry condition (BOLev_D only) and surfaced
   *  on the chart as an additional plotshape on the BOLev_D / BOLev_W overlay. */
  cond3Buy: boolean;
  cond3Short: boolean;
}

export interface BolevResult {
  periods: BolevPeriod[];
}

/** Plain-English read of the current state for the crosshair tooltip. */
export interface BolevHeadline {
  label: string;                                       // short state name
  tone: 'bull' | 'bear' | 'neutral' | 'squeeze';      // colour hint
  guidance: string;                                    // what to do, for an end user
}
export function bolevHeadline(
  buy: BolevState,
  short: BolevState,
  lookback: number,
  cond3Buy = false,
  cond3Short = false,
): BolevHeadline {
  const L = `${lookback}-candle`;
  // R115 — Cond 3 takes the headline when it fires: a strong no-wick CLH candle
  // that also broke the level. Conviction is higher than a normal lookout-zone
  // trigger so it is mentioned ahead of buy/short state.
  if (cond3Buy)   return { label: 'COND3 BUY',   tone: 'bull', guidance: `Strong upside break — CLH candle has no lower wick AND high cleared the ${L} high. High-conviction LONG.` };
  if (cond3Short) return { label: 'COND3 SHORT', tone: 'bear', guidance: `Strong downside break — CLH candle has no upper wick AND low broke the ${L} low. High-conviction SHORT.` };
  if (buy === 'zone') return { label: 'BUY ZONE', tone: 'bull', guidance: `Upside breakout — price cleared the ${L} high. Bias UP: look to go / stay LONG. Fails if price drops back below the BUY level.` };
  if (short === 'zone') return { label: 'SHORT ZONE', tone: 'bear', guidance: `Downside breakdown — price broke the ${L} low. Bias DOWN: look to go / stay SHORT. Fails if price climbs back above the SHORT level.` };
  if (buy === 'lookout' && short === 'lookout') return { label: 'SQUEEZE', tone: 'squeeze', guidance: `Both sides armed — coiled. Trade the breakout: LONG above the BUY level, SHORT below the SHORT level.` };
  if (buy === 'lookout') return { label: 'BUY LOOKOUT', tone: 'bull', guidance: `Watching for an upside breakout. Go LONG only if price breaks above the BUY level; no SHORT setup armed.` };
  if (short === 'lookout') return { label: 'SHORT LOOKOUT', tone: 'bear', guidance: `Watching for a downside breakdown. Go SHORT only if price breaks below the SHORT level; no BUY setup armed.` };
  return { label: 'IDLE', tone: 'neutral', guidance: `No active breakout setup. Wait for a level to arm before trading.` };
}

/**
 * @param rawPeriodCandles  RAW Daily (or Weekly) OHLC candles, oldest→newest,
 *                          IST-shifted (so times align with the chart grid).
 * @param lookback          how many completed candles feed the level (default 2).
 */
export function computeBolev(rawPeriodCandles: CandleRow[], lookback = 2): BolevResult {
  const periods: BolevPeriod[] = [];
  if (!rawPeriodCandles || rawPeriodCandles.length === 0) return { periods };
  const lb = Math.max(1, Math.floor(lookback));
  const clh = transformCandles(rawPeriodCandles, 'CLH');
  const n = clh.length;

  let buy: BolevState = 'off';
  let short: BolevState = 'off';

  for (let i = 0; i < n; i++) {
    const c = clh[i];
    // Levels from the previous `lb` COMPLETED candles (i-1 .. i-lb).
    let buyLevel: number | null = null;
    let shortLevel: number | null = null;
    if (i >= lb) {
      let mx = -Infinity, mn = Infinity;
      for (let k = 1; k <= lb; k++) {
        if (clh[i - k].high > mx) mx = clh[i - k].high;
        if (clh[i - k].low < mn) mn = clh[i - k].low;
      }
      buyLevel = Number.isFinite(mx) ? mx : null;
      shortLevel = Number.isFinite(mn) ? mn : null;
    }

    // Breakout test DURING this period (only if the side is armed). BUY wins a
    // same-period tie (it's evaluated first and flips SHORT off).
    let buyBreak = false, shortBreak = false;
    if (buy === 'lookout' && buyLevel != null && c.high > buyLevel) {
      buy = 'zone'; buyBreak = true; short = 'off';
    }
    if (short === 'lookout' && shortLevel != null && c.low < shortLevel) {
      short = 'zone'; shortBreak = true; buy = 'off';
    }

    // R115 Cond 3 — strict no-wick + level-break (independent of state machine).
    // CLH guarantees high = max(rawHigh, open, close) and low = min(rawLow, open, close),
    // so `low === open` ⟺ no lower wick AND a bullish body (open ≤ close); symmetric
    // for `high === open`. No float epsilon needed — the equality is exact.
    const cond3Buy   = c.low  === c.open && buyLevel   != null && c.high > buyLevel;
    const cond3Short = c.high === c.open && shortLevel != null && c.low  < shortLevel;

    periods.push({
      time: c.time, buyLevel, shortLevel, buy, short, buyBreak, shortBreak,
      high: c.high, low: c.low, cond3Buy, cond3Short,
    });

    // Arm / re-arm lookouts from THIS candle's close, for the NEXT period.
    // open == high (opened at the top) arms BUY; open == low arms SHORT.
    const openIsHigh = c.open >= c.high;
    const openIsLow = c.open <= c.low;
    if (openIsHigh) buy = 'lookout';
    if (openIsLow) short = 'lookout';
  }

  return { periods };
}
